// Session Timeout Warning Modal
// Shows a countdown when the user's session is about to expire due to inactivity

import { useAuthStore } from '../stores/auth-store';

interface SessionTimeoutWarningProps {
  remainingSeconds: number;
  onStayLoggedIn: () => void;
  onLogout: () => void;
}

function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function SessionTimeoutWarning({
  remainingSeconds,
  onStayLoggedIn,
  onLogout,
}: SessionTimeoutWarningProps) {
  const { logout } = useAuthStore();

  const handleLogout = () => {
    onLogout();
    logout();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
            <svg
              className="h-5 w-5 text-amber-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Session Expiring</h3>
        </div>

        <p className="mb-2 text-sm text-gray-600">
          Your session will expire due to inactivity in:
        </p>

        <div className="mb-6 text-center">
          <span className="text-4xl font-bold tabular-nums text-amber-600">
            {formatTime(remainingSeconds)}
          </span>
        </div>

        <p className="mb-6 text-sm text-gray-500">
          Click below to stay logged in, or your session will end automatically.
        </p>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleLogout}
            className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Log Out
          </button>
          <button
            type="button"
            onClick={onStayLoggedIn}
            className="btn-primary flex-1"
          >
            Stay Logged In
          </button>
        </div>
      </div>
    </div>
  );
}
